import {formatCoordinateForDisplay} from '../../../shared/utils/coordinateParsing'

type FieldLike = {fieldType?: string; label?: string}

const CONSUMPTION_MULTI_ROW_LABELS = new Set([
  'fuel type',
  'rob',
  'machinery',
  'consumed',
  'operation type',
  'initial rob',
])

export const isMultiSelectField = (field?: FieldLike): boolean =>
  String(field?.fieldType ?? '')
    .trim()
    .toLowerCase() === 'checkbox'

export const isConsumptionMultiRowFieldLabel = (label?: string): boolean =>
  CONSUMPTION_MULTI_ROW_LABELS.has(String(label ?? '').trim().toLowerCase())

const tryParseJson = (raw: string): unknown => {
  try {
    return JSON.parse(raw)
  } catch {
    return raw
  }
}

/** Formats position coordinates or ISO datetimes for presentation */
export const formatDisplayScalar = (str: string, field?: FieldLike): string => {
  if (!str) return ''

  // 1. Check if it's a coordinate/position string (e.g. "Latitude: 8 Deg 8 min 8 sec N | Longitude: 8 Deg 8 min 8 sec E")
  if (
    /Latitude:\s*[\d.]+\s*(?:Deg|°)/i.test(str) ||
    /\b(?:deg|°)\b.*\b(?:min|'|′)\b.*\b(?:sec|"|″)\b/i.test(str) ||
    String(field?.fieldType ?? '').toLowerCase() === 'position' ||
    /\b(?:position|latitude|longitude)\b/i.test(String(field?.label ?? ''))
  ) {
    const formattedCoord = formatCoordinateForDisplay(str)
    if (formattedCoord && formattedCoord !== '--' && formattedCoord !== str) {
      return formattedCoord
    }
  }

  // 2. Check if it's an ISO datetime string (e.g. "2026-10-03T13:42" or "2026-10-03T13:42:00")
  const isoDtMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/)
  if (isoDtMatch) {
    const [, y, m, d, hh, mm, ss] = isoDtMatch
    const timePart = ss ? `${hh}:${mm}:${ss}` : `${hh}:${mm}`
    return `${d}/${m}/${y} ${timePart}`
  }

  return str
}

/** Unwrap single-element JSON arrays like '["0"]' for scalar display. */
export const normalizeScalarForDisplay = (raw: unknown, field?: FieldLike): string => {
  if (raw === null || raw === undefined) return ''
  if (raw === '') return ''

  if (isMultiSelectField(field)) {
    if (Array.isArray(raw)) return raw.map(String).join(', ')
    return String(raw)
  }

  if (field && isConsumptionMultiRowFieldLabel(field.label)) {
    return formatConsumptionStoredValue(raw)
  }

  let value: unknown = raw
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
      value = tryParseJson(trimmed)
    } else {
      return formatDisplayScalar(trimmed, field)
    }
  }

  if (Array.isArray(value)) {
    if (value.length === 1 && !Array.isArray(value[0])) {
      return formatDisplayScalar(String(value[0] ?? ''), field)
    }
    if (value.length === 0) return ''
    return value.map((item) => (Array.isArray(item) ? item.join(', ') : String(item))).join('; ')
  }

  return formatDisplayScalar(String(value), field)
}

/** Format consumption tab values persisted as JSON arrays in report_field_value. */
export const formatConsumptionStoredValue = (raw: unknown, rowIndex = 0): string => {
  if (raw === null || raw === undefined || raw === '') return ''

  let parsed: unknown = raw
  if (typeof raw === 'string') {
    const trimmed = raw.trim()
    if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
      parsed = tryParseJson(trimmed)
    } else {
      return trimmed
    }
  }

  if (!Array.isArray(parsed)) {
    return String(parsed ?? '')
  }

  const row = parsed[rowIndex]
  if (Array.isArray(row)) return row.map(String).join(', ')
  return String(row ?? '')
}

/** Numeric consumption API fields that may arrive as JSON-encoded strings. */
export const formatConsumptionMetric = (raw: unknown): string => {
  if (raw === null || raw === undefined || raw === '') return '--'
  const normalized = normalizeScalarForDisplay(raw)
  return normalized === '' ? '--' : capDecimalsToTwo(normalized)
}

/**
 * Cap a numeric-looking value at 2 decimal places (rounded, trailing zeros trimmed).
 * Non-numeric text is returned unchanged so labels/grades are preserved.
 */
export const capDecimalsToTwo = (raw: unknown): string => {
  if (raw === null || raw === undefined) return ''
  const str = String(raw).trim()
  if (str === '') return ''
  const num = Number(str)
  if (!Number.isFinite(num)) return str
  return String(Number(num.toFixed(2)))
}
